import { DRILLDOWN_PANEL_INITIAL_DATA } from 'features/drilldown/components/panelConfigs/PanelConfigs'
import { DrilldownPanelType } from 'features/drilldown/types'
import { z } from 'zod'

export const EXPLORATION_MUTATION_VERSION = 1

type JsonPrimitive = string | number | boolean | null
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue }
type JsonRecord = { [key: string]: JsonValue }
type AllowedPanelType = Exclude<DrilldownPanelType, 'llmPanel'>

const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ])
)

const jsonRecordSchema: z.ZodType<JsonRecord> = z.record(
  z.string(),
  jsonValueSchema
)

const isAllowedPanelType = (value: unknown): value is AllowedPanelType =>
  typeof value === 'string' &&
  value in DRILLDOWN_PANEL_INITIAL_DATA &&
  value !== 'llmPanel'

const allowedPanelTypeSchema = z.custom<AllowedPanelType>(isAllowedPanelType)

const completePanelDataSchema = z
  .object({
    panelDataSchemaVersion: z.number(),
    isPinned: z.boolean(),
    title: z.string(),
    isConfigOpen: z.boolean().optional(),
  })
  .catchall(jsonValueSchema)

const createOperationSchema = z
  .object({
    op: z.literal('create'),
    panel: z
      .object({
        panelType: allowedPanelTypeSchema,
        initialDataOverride: jsonRecordSchema.optional(),
      })
      .strict(),
    after_panel_id: z.string().optional(),
  })
  .strict()

const updateOperationSchema = z
  .object({
    op: z.literal('update'),
    panel_id: z.string(),
    panel_data: completePanelDataSchema,
  })
  .strict()

const removeOperationSchema = z
  .object({
    op: z.literal('remove'),
    panel_id: z.string(),
  })
  .strict()

const explorationMutationSchema = z
  .object({
    version: z.literal(EXPLORATION_MUTATION_VERSION),
    dashboard_id: z.string(),
    revision: z.string(),
    request_id: z.string(),
    operations: z
      .array(
        z.discriminatedUnion('op', [
          createOperationSchema,
          updateOperationSchema,
          removeOperationSchema,
        ])
      )
      .min(1),
  })
  .strict()

export type ExplorationMutation = z.infer<typeof explorationMutationSchema>

export const parseExplorationMutation = (
  value: unknown
): ExplorationMutation | undefined => {
  const parsed = explorationMutationSchema.safeParse(value)
  return parsed.success ? parsed.data : undefined
}
